import type { CSSProperties, ReactNode } from "react";
import { card, countTag, theme } from "../theme";

// ── The drawing-sheet language ────────────────────────────────────────────
// Pages are sheets laid on engineering paper (index.css .paper-grid). These are the pieces every
// page builds from, so a sheet, a heading, a score and a status look the same everywhere:
//
//   <SheetCard>        a card with a title strip: what it is on the left, its reference on the right
//   <DimensionHeading> a section heading with a dimension line running to its count
//   <RulerProgress>    a score on a ruled scale with a marker, instead of a rounded bar
//   <StatusTag>        a status carried by shape as well as colour (readable without colour vision)
//   <IndexTabs>        tabs inside a page or panel (the shell's own tabs are nav/AreaTabs.tsx)
//
// Colours come from ThemeContext.tsx: --c-line (frame ink), --c-mark (the "you are here" marker),
// --c-ref (references), so the main app (plumage) and the Jersey edition (wing) follow by palette.

/** The small square ticks on two opposite corners of a featured sheet. */
function CornerTicks() {
  const tick: CSSProperties = { position: "absolute", width: 9, height: 9, pointerEvents: "none" };
  return (
    <>
      <span aria-hidden="true" style={{ ...tick, left: -5, top: -5, borderLeft: "1px solid var(--c-mark)", borderTop: "1px solid var(--c-mark)" }} />
      <span aria-hidden="true" style={{ ...tick, right: -5, bottom: -5, borderRight: "1px solid var(--c-mark)", borderBottom: "1px solid var(--c-mark)" }} />
    </>
  );
}

/**
 * A drawing sheet with a title strip. `label` is the area it belongs to (shown small and spaced),
 * `reference` what it is filed under (a control id, a record ref, a revision). `featured` adds the
 * corner ticks — keep those for the one or two sheets a page leads with.
 */
export function SheetCard({ label, reference, actions, featured, children, padding = 16, style }: {
  label?: ReactNode;
  reference?: ReactNode;
  actions?: ReactNode;
  featured?: boolean;
  children: ReactNode;
  padding?: number | string;
  style?: CSSProperties;
}) {
  const hasStrip = label != null || reference != null || actions != null;
  return (
    <section style={{ ...card, padding: 0, position: "relative", ...style }}>
      {hasStrip && (
        <header style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 12px", borderBottom: "1px solid var(--c-line)", fontFamily: theme.font, minHeight: 30 }}>
          {label != null && (
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--c-heading)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
          )}
          {reference != null && (
            <span style={{ fontSize: 11, color: "var(--c-ref)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", marginLeft: label == null ? "auto" : undefined }}>{reference}</span>
          )}
          {actions}
        </header>
      )}
      <div style={{ padding }}>{children}</div>
      {featured && <CornerTicks />}
    </section>
  );
}

/** A section heading with a dimension line running from it to an end tick and, optionally, a count. */
export function DimensionHeading({ children, count, as: Tag = "h2", style }: {
  children: ReactNode;
  count?: number | string;
  as?: "h2" | "h3" | "h4";
  style?: CSSProperties;
}) {
  return (
    <Tag style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: "var(--c-heading)", lineHeight: 1.35, fontFamily: theme.font, ...style }}>
      <span style={{ flexShrink: 0 }}>{children}</span>
      <span aria-hidden="true" style={{ flex: 1, height: 0, borderTop: "1px solid var(--c-mark)", position: "relative", minWidth: 24 }}>
        <span style={{ position: "absolute", right: 0, top: -5, height: 9, borderLeft: "1px solid var(--c-mark)" }} />
      </span>
      {count != null && (
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--c-muted)", fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{count}</span>
      )}
    </Tag>
  );
}

/**
 * A value on a ruled scale: ten divisions, the filled length in the accent and a marker needle at
 * the value. `max` defaults to 100. Announced as a progressbar.
 */
export function RulerProgress({ value, max = 100, label, height = 14, fill: fillColor = "var(--c-accent)", style }: {
  value: number;
  max?: number;
  label?: string;
  height?: number;
  /** The filled length's colour; pass a score band's colour where the bar carries one. */
  fill?: string;
  style?: CSSProperties;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  const fill = Math.max(4, Math.round(height * 0.4));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      aria-label={label}
      style={{
        position: "relative", height,
        borderBottom: "1px solid var(--c-line)",
        background: `repeating-linear-gradient(90deg, var(--c-line) 0 1px, transparent 1px 10%) bottom / 100% ${fill + 2}px no-repeat`,
        ...style,
      }}
    >
      <div style={{ position: "absolute", left: 0, bottom: 0, width: `${pct}%`, height: fill, background: fillColor, opacity: 0.85 }} />
      <div style={{ position: "absolute", left: `${pct}%`, bottom: 0, height, borderLeft: "2px solid var(--c-mark)", transform: "translateX(-1px)" }} />
    </div>
  );
}

export interface IndexTabItem<T extends string> {
  id: T;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

/**
 * Tabs inside a page or a panel, drawn as index tabs: the current one is cut from the ground
 * below it with the marker along its top edge, the others sit behind. `ground` is the colour of
 * what the tabs sit on — the page (default) or a sheet (`var(--c-panel)`).
 */
export function IndexTabs<T extends string>({ items, value, onChange, ground = "var(--c-bg)", label, style }: {
  items: IndexTabItem<T>[];
  value: T;
  onChange: (id: T) => void;
  ground?: string;
  label?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      style={{ display: "flex", alignItems: "flex-end", gap: 3, overflowX: "auto", boxShadow: "inset 0 -1px 0 var(--c-line)", marginBottom: 14, scrollbarWidth: "thin", ...style }}
    >
      {items.map((t) => {
        const current = t.id === value;
        return (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={current}
            disabled={t.disabled}
            onClick={() => onChange(t.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: current ? "7px 13px 8px" : "5px 11px 6px",
              fontSize: 13, fontWeight: current ? 600 : 500, fontFamily: theme.font,
              color: current ? "var(--c-heading)" : "var(--c-muted)",
              background: current ? ground : "var(--c-panelAlt)",
              border: `1px solid ${current ? "var(--c-line)" : "var(--c-border)"}`,
              borderBottom: current ? "none" : "1px solid var(--c-line)",
              borderRadius: "3px 3px 0 0",
              boxShadow: current ? "inset 0 3px 0 var(--c-mark)" : undefined,
              cursor: t.disabled ? "default" : "pointer", opacity: t.disabled ? 0.5 : 1,
              whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            {t.label}
            {t.count != null && <span style={countTag}>{t.count > 99 ? "99+" : t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export type StatusTone = "met" | "partial" | "gap" | "none";

// The shape is the meaning; the colour repeats it. Filled circle met, half circle part-way,
// triangle a gap, open circle not yet looked at.
const STATUS_SHAPE: Record<StatusTone, { glyph: string; color: string }> = {
  met:     { glyph: "●", color: "var(--c-successText)" },
  partial: { glyph: "◐", color: "var(--c-warningText)" },
  gap:     { glyph: "▲", color: "var(--c-dangerText)" },
  none:    { glyph: "○", color: "var(--c-muted)" },
};

/** An outlined, square-cornered status tag led by its shape. */
export function StatusTag({ tone, children, title }: { tone: StatusTone; children: ReactNode; title?: string }) {
  const s = STATUS_SHAPE[tone];
  return (
    <span
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "1px 7px", borderRadius: 2, fontSize: 11, fontWeight: 600, lineHeight: "17px",
        color: s.color, border: `1px solid ${s.color}`, background: "var(--c-panel)",
        flexShrink: 0, whiteSpace: "nowrap", fontFamily: theme.font,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 12, lineHeight: 1 }}>{s.glyph}</span>
      {children}
    </span>
  );
}
